import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { authApi } from '../api';
import type { User, Account, AccountSummary, Permission, LoginResponse } from '../types/auth';
import * as storage from '../utils/storage';
import { hasPermission as checkPermission, parsePermission } from '../utils/permissions';

/**
 * Authentication state
 */
export interface AuthState {
  // User information
  user: User | null;
  currentAccount: Account | null;
  accounts: AccountSummary[];

  // JWT token
  token: string | null;
  permission: Permission | null;
  expiresAt: Date | null;

  // Status
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Authentication context type
 */
interface AuthContextType extends AuthState {
  // Auth actions
  login: (email: string, password: string, accountId?: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchAccount: (accountId: string, password: string) => Promise<void>;

  // Permission checks
  hasPermission: (required: Permission) => boolean;
  canAccessRoute: (requiredPermission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provider component for authentication state
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // Initialize from localStorage
    const token = storage.getToken();
    const user = storage.getUser();
    const currentAccount = storage.getCurrentAccount();
    const accounts = storage.getAccounts();
    const permission = storage.getPermission();
    const expiresAt = storage.getExpiresAt();

    // Check if authenticated (has token and refresh token not expired)
    const refreshToken = storage.getRefreshToken();
    const isAuthenticated = !!(token && refreshToken);

    return {
      user,
      currentAccount,
      accounts,
      token,
      permission,
      expiresAt,
      isLoading: false,
      isAuthenticated,
    };
  });

  // Track whether a proactive refresh is in flight
  const isRefreshing = useRef(false);

  // Track whether auth state was just set by login/signup (skip immediate check)
  const justAuthenticated = useRef(false);

  /**
   * Store login response in state and localStorage
   */
  const storeAuthData = useCallback((response: LoginResponse) => {
    const user: User = {
      id: response.user_id,
      email: response.email,
      is_superadmin: response.is_superadmin,
      approved: response.approved ?? true,
    };

    // Build current account from login response
    // Since commit 576d3ce the backend always returns a scoped account_id
    const matchedAccount = response.accounts.find(a => a.id === response.account_id);

    const currentAccount: Account | null = response.account_id
      ? {
          id: response.account_id,
          account_id: matchedAccount?.account_id || '',
          permission: response.permission ? parsePermission(response.permission) : 'management',
        }
      : null;

    const permission: Permission | null = response.permission
      ? parsePermission(response.permission)
      : (response.is_superadmin ? 'management' : null);

    const expiresAt = new Date(response.expires_at);

    // Store in localStorage
    storage.setToken(response.token);
    storage.setRefreshToken(response.refresh_token);
    storage.setUser(user);
    if (currentAccount) {
      storage.setCurrentAccount(currentAccount);
    }
    storage.setAccounts(response.accounts);
    if (permission) {
      storage.setPermission(permission);
    }
    storage.setExpiresAt(expiresAt);
    storage.setRefreshExpiresAt(new Date(response.refresh_expires_at));

    // Mark that we just authenticated — skip the immediate proactive refresh check
    justAuthenticated.current = true;

    // Update state
    setAuthState({
      user,
      currentAccount,
      accounts: response.accounts,
      token: response.token,
      permission,
      expiresAt,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string, accountId?: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const response = await authApi.login(email, password, accountId);
      storeAuthData(response);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [storeAuthData]);

  /**
   * Sign up with email and password
   */
  const signup = useCallback(async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const response = await authApi.signup(email, password);

      // Convert signup response to login response format
      const loginResponse: LoginResponse = {
        user_id: response.user_id,
        email: response.email,
        is_superadmin: false,
        approved: response.approved ?? false,
        token: response.token,
        refresh_token: response.refresh_token,
        account_id: response.account_id,
        permission: 'management',  // Default account owner has management
        expires_at: response.expires_at,
        refresh_expires_at: response.refresh_expires_at,
        accounts: [{
          id: response.account_id,
          account_id: response.account_identifier,
          permission: 'management',
        }],
      };

      storeAuthData(loginResponse);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [storeAuthData]);

  /**
   * Logout and clear all authentication data
   */
  const logout = useCallback(() => {
    // Best-effort server-side logout (revoke refresh token family)
    const refreshToken = storage.getRefreshToken();
    if (refreshToken) {
      authApi.logout(refreshToken);
    }

    storage.clearAuth();
    setAuthState({
      user: null,
      currentAccount: null,
      accounts: [],
      token: null,
      permission: null,
      expiresAt: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  /**
   * Switch to a different account
   * Superadmins can switch without password re-authentication
   */
  const switchAccount = useCallback(async (accountId: string, password: string) => {
    if (!authState.user) {
      throw new Error('Not authenticated');
    }

    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));

      // Login again with the new account ID (backend returns new JWT)
      const response = await authApi.login(authState.user.email, password, accountId);
      storeAuthData(response);
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [authState.user, storeAuthData]);

  /**
   * Check if user has required permission
   */
  const hasPermission = useCallback((required: Permission): boolean => {
    // Superadmins always have all permissions
    if (authState.user?.is_superadmin) return true;
    if (!authState.permission) return false;
    return checkPermission(authState.permission, required);
  }, [authState.permission, authState.user?.is_superadmin]);

  /**
   * Check if user can access a route with required permission
   */
  const canAccessRoute = useCallback((requiredPermission: Permission): boolean => {
    return authState.isAuthenticated && hasPermission(requiredPermission);
  }, [authState.isAuthenticated, hasPermission]);

  /**
   * Proactive token refresh: check every minute and refresh when expiring soon.
   * This ensures permission changes are picked up from the DB without requiring logout.
   */
  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const checkAndRefresh = async () => {
      if (isRefreshing.current) return;

      const refreshToken = storage.getRefreshToken();
      if (!refreshToken) {
        logout();
        return;
      }

      // Check if access token is expiring soon (within 2 minutes)
      if (storage.isTokenExpiringSoon()) {
        isRefreshing.current = true;
        try {
          const result = await authApi.refresh(refreshToken);

          // Update tokens in storage
          storage.setToken(result.token);
          storage.setRefreshToken(result.refresh_token);
          storage.setExpiresAt(new Date(result.expires_at));
          storage.setRefreshExpiresAt(new Date(result.refresh_expires_at));

          // Update permission and approval status from DB
          const newPermission = result.permission ? parsePermission(result.permission) : null;
          if (newPermission) {
            storage.setPermission(newPermission);
          }

          // Update user approval/superadmin status
          const updatedUser = storage.getUser();
          if (updatedUser) {
            updatedUser.approved = result.approved;
            updatedUser.is_superadmin = result.is_superadmin;
            storage.setUser(updatedUser);
          }

          // Update state with refreshed data
          setAuthState(prev => ({
            ...prev,
            token: result.token,
            expiresAt: new Date(result.expires_at),
            permission: newPermission ?? prev.permission,
            user: updatedUser ?? prev.user,
          }));
        } catch {
          // Refresh failed — session is invalid
          logout();
        } finally {
          isRefreshing.current = false;
        }
      }
    };

    // Check immediately on mount (but skip if we just logged in — the token is fresh)
    if (justAuthenticated.current) {
      justAuthenticated.current = false;
    } else {
      checkAndRefresh();
    }

    // Check every minute
    const interval = setInterval(checkAndRefresh, 60 * 1000);

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, logout]);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        signup,
        logout,
        switchAccount,
        hasPermission,
        canAccessRoute,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
